/** @format */

import {
  Chain,
  Environment,
  ISkuLocation,
  SkuLocationResponse,
  StoreInformation,
  AvailabilityByLocationResponse,
  SameDayDeliveryResponse,
  StoreInformationV3,
  StoresByLocationResponse,
} from '@shared/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, retry } from 'rxjs/operators';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { Injectable, inject } from '@angular/core';
import { KillswitchService } from '../killswitch/killswitch.service';
import { Logger } from '../logger.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StoreDao {
  private readonly chain = inject(Chain);
  private readonly environment = inject(Environment);
  private readonly http = inject(HttpClient);
  private readonly killswitchService = inject(KillswitchService);
  private readonly logger = inject(Logger);
  private readonly isAvailabilityApiBrandDomainEnabled =
    this.killswitchService.killSwitchForCurrentChain('enableAvailabilityApiBrandDomain');

  private getApiBrandDomainRequestConfig(
    path: string,
    httpHeaders?: HttpHeaders,
    params?: HttpParams,
    api: 'availability' | 'inventory' = 'availability',
  ) {
    const enabled = this.isAvailabilityApiBrandDomainEnabled();
    const key = api === 'inventory' ? 'inventoryByChain' : 'availabilityByChain';
    const brandUrl = this.environment.api.elastic?.[key];
    const baseUrl = enabled && brandUrl ? brandUrl : this.environment.api.elastic?.[api];

    if (enabled && !brandUrl) {
      this.logger.warn(`Missing api.elastic.${key}; using api.elastic.${api}`);
    }

    return {
      url: `${baseUrl}${path}`,
      options: {
        headers: httpHeaders,
        params,
      },
    };
  }

  /**
   * @description
   * Gets a list of stores within the search radius of the provided coordinates
   */
  public getAllStoresWithinRadius$(
    latitude: number | string,
    longitude: number | string,
    zipCode: string | undefined,
    retries = 0,
    searchRadius = DCSG_CONSTANTS.defaultStoreSearchRadius,
    useRadius?: boolean,
  ): Observable<StoresByLocationResponse> {
    // Default store fronts to current chain
    let storeFronts = this.chain.chainIdentifierAbbr;

    // Check if cross-channel BOPIS is enabled
    if (
      this.environment.killSwitch?.crossChainBopis?.[this.chain.chainIdentifierAbbr] &&
      this.chain.crossChainBopisPartners
    ) {
      // Append the other partners
      storeFronts += `,${this.chain.crossChainBopisPartners.toString()}`;
    }

    // default to lat and long
    let addrString = `${latitude},${longitude}`;

    // Check to see if there are valid geo coordinates
    if (zipCode && (latitude === 0 || latitude === '0')) {
      addrString = zipCode;
    }

    let path = `/api/v3/stores/search?addr=${addrString}&lob=${storeFronts}`;

    if (useRadius) {
      path = `${path}&radius=${searchRadius}`;
    }

    const { url, options } = this.getApiBrandDomainRequestConfig(path);

    // Make the HTTP Request
    return this.http.get<StoresByLocationResponse>(url, options).pipe(retry(retries));
  }

  /**
   * @description
   * Accepts SKU(s) and location and returns inventory details for the supplied SKU(s) by given location
   */
  public getSkuInventoryByLocation$(
    skuData: number | string | number[] | string[],
    location: number | string | number[] | string[],
    retries = 0,
  ): Observable<ISkuLocation[]> {
    const httpParams = new HttpParams()
      .set('location', Array.isArray(location) ? location.toString() : [location].toString())
      .set('sku', Array.isArray(skuData) ? skuData.toString() : [skuData].toString());

    // Standard HTTP Headers
    let httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
      'X-TIMEOUT': `${DCSG_CONSTANTS.timeouts.medium}`,
    });

    let apiVersion = 'v1';

    // make sure new api is enabled and new token is defined
    // obtain header value from EAT

    if (this.environment.killSwitch.newEATApiEnabled[this.chain.chainIdentifierAbbr]) {
      apiVersion = 'v2';
      httpHeaders = httpHeaders.set(
        'X-API-Key',
        this.environment.apiTokens?.elastic?.availability ?? '',
      );
    }

    // HTTP Options
    const { url, options } = this.getApiBrandDomainRequestConfig(
      `/${apiVersion}/inventoryapis/searchinventory`,
      httpHeaders,
      httpParams,
      'inventory',
    );

    // Make the HTTP Request
    return this.http.get<SkuLocationResponse>(url, options).pipe(
      map((response) => {
        // Return data
        let returnData: ISkuLocation[] = [];

        // Check to see if there is any SKU inventory data
        if (response.data?.skus !== undefined) {
          // Set the return data
          returnData = response.data.skus;
        }

        // Return the raw data
        return returnData;
      }),
      retry(retries),
    );
  }

  /**
   * @description
   * Gets a list of stores within the search radius for the selected Sku
   */
  public getStoreInformation$(
    zipcode: string,
    productSku: string | string[],
    retries = 0,
  ): Observable<AvailabilityByLocationResponse> {
    // Default store fronts to current chain
    let storeFronts = this.chain.chainMarketingIdentifierAbbr;

    // Check if cross-channel BOPIS is enabled
    if (
      this.environment.killSwitch.crossChainBopis[this.chain.chainIdentifierAbbr] &&
      this.chain.crossChainBopisPartners
    ) {
      // Append the other partners
      storeFronts += `,${this.chain.crossChainBopisPartners.toString()}`;
    }

    // Query String Parameters
    const httpParams = new HttpParams()
      .set('addr', `${zipcode}`)
      .set('radius', `${DCSG_CONSTANTS.defaultStoreSearchRadius}`)
      .set('uom', 'imperial')
      .set('lob', storeFronts)
      .set('sku', `${productSku}`)
      .set('res', 'locatorsearch');

    // HTTP Headers
    let httpHeaders = new HttpHeaders({
      Accept: MediaType.WILDCARD,
      'X-TIMEOUT': `${DCSG_CONSTANTS.timeouts.medium}`,
    });

    // Custom HTTP Headers
    httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

    let apiVersion = 'v3';

    // make sure new api is enabled and new token is defined
    // obtain header value from EAT
    if (this.environment.killSwitch.newEATApiEnabled[this.chain.chainIdentifierAbbr]) {
      apiVersion = 'v4';
      httpHeaders = httpHeaders.set(
        'X-API-Key',
        this.environment.apiTokens?.elastic?.availability ?? '',
      );
    }

    // HTTP Options
    const { url, options } = this.getApiBrandDomainRequestConfig(
      `/ws/${apiVersion}/omni/stores`,
      httpHeaders,
      httpParams,
    );

    // Make the HTTP Request
    return this.http.get<AvailabilityByLocationResponse>(url, options).pipe(retry(retries));
  }

  /**
   * @description
   * Returns information for a specific store by number
   *
   * @returns - {Observable<StoreInformation>}
   */
  // Note: Below function is also being used by AOS Store modal to determine if valid store number is entered
  public getBopisStoreInformation$(storeNumber: string): Observable<StoreInformation> {
    // HTTP Headers
    const httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
      'Content-Type': MediaType.APPLICATION_JSON,
      'Access-Control-Allow-Origin': '*',
      'X-TIMEOUT': `${DCSG_CONSTANTS.timeouts.medium}`,
    });

    const { url, options } = this.getApiBrandDomainRequestConfig(
      `/api/v1/stores/${storeNumber}`,
      httpHeaders,
    );

    // Make the HTTP Request
    return this.http.get<StoreInformation>(url, options).pipe(retry(1));
  }

  public getBopisStoreInformationV3$(storeNumber: string): Observable<StoreInformationV3> {
    // HTTP Headers
    const httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
      'Content-Type': MediaType.APPLICATION_JSON,
      'Access-Control-Allow-Origin': '*',
      'X-TIMEOUT': `${DCSG_CONSTANTS.timeouts.medium}`,
    });
    const { url, options } = this.getApiBrandDomainRequestConfig(
      `/api/v3/stores/${storeNumber}`,
      httpHeaders,
    );

    // Make the HTTP Request
    return this.http.get<StoreInformationV3>(url, options).pipe(retry(1));
  }

  public getSameDayAvailability$(
    zipCode = '',
    skus: string[] = [],
  ): Observable<SameDayDeliveryResponse> {
    // Standard HTTP Headers
    const httpHeaders = new HttpHeaders({
      'X-TIMEOUT': DCSG_CONSTANTS.timeouts.medium,
      'X-API-Key': `${this.environment.apiTokens?.elastic?.availability}`,
    });

    let skuParams = '';
    if (skus.length) {
      for (const sku of skus) {
        skuParams += `&skus=${sku}`;
      }
    }

    const { url, options } = this.getApiBrandDomainRequestConfig(
      `/v1/same-day-availability/zipcode/sku?zipCode=${zipCode}${skuParams}`,
      httpHeaders,
    );

    return this.http.get<SameDayDeliveryResponse>(url, options).pipe(retry(1));
  }
}
